'use client';

import { useCallback } from 'react';
import clsx from 'clsx';
import { Env } from '@/libs/env';

interface DotButtonProps {
  totalSlides: number;
  currentSlide: number;
  onSlideChange: (index: number) => void;
  className?: string;
}

export default function DotButton({
  totalSlides,
  currentSlide,
  onSlideChange,
  className
}: DotButtonProps) {
  const goToSlide = useCallback(
    (index: number) => {
      onSlideChange(index);
    },
    [onSlideChange]
  );

  const useComeStyle = Env.NEXT_PUBLIC_USE_COME_DOT_STYLE === 'true';

  // 根据 brand 决定渲染模式：come 盘使用 C+O*n+M+E，其他盘仅使用 O*n
  const generateIndicators = (): string[] => {
    if (useComeStyle) {
      const indicators: string[] = ['C'];
      for (let i = 0; i < totalSlides; i++) {
        indicators.push('O');
      }
      indicators.push('M', 'E');
      return indicators;
    }
    return Array(totalSlides).fill('O');
  };

  const indicators = generateIndicators();

  // 判断当前字母是否应该高亮（只有O字母且对应的slide是当前slide时才高亮）
  const isHighlighted = (letter: string, index: number): boolean => {
    if (letter !== 'O') {
      return false;
    }
    // come 盘：O 字母的索引从1开始，减去 C 的位置；其他盘：直接对应索引
    const oIndex = useComeStyle ? index - 1 : index;
    return oIndex === currentSlide;
  };

  const letterColors = [
    'text-accent-blue',
    'text-success',
    'text-pending',
    'text-accent-red',
  ];

  // come盘：O 从 index 1 开始（index 0 是 C），需减 1 对齐到颜色数组
  const getLetterColor = (index: number) => {
    return letterColors[(index - 1) % letterColors.length];
  };

  // 非come盘：O 从 index 0 开始，直接取余
  const getNonComeLetterColor = (index: number) => {
    return letterColors[index % letterColors.length];
  };

  return (
    <div className={clsx('flex items-center h-[6px] space-x-[0.5px]', className)}>
      {indicators.map((letter, index) => {
        // 只有O字母可以点击，且点击时跳转到对应的slide
        const isO = letter === 'O';
        const oIndex = isO ? (useComeStyle ? index - 1 : index) : null;

        return (
          <button
            key={index}
            onClick={() => {
              if (isO && oIndex !== null) {
                goToSlide(oIndex);
              }
            }}
            className={clsx(
              'text-[8px] font-black transition-all duration-200',
              isHighlighted(letter, index)
                ? useComeStyle
                  ? getLetterColor(index)
                  : getNonComeLetterColor(index)
                : 'text-[#f4edff] opacity-[0.16]' // 非活跃状态
            )}
          >
            {letter}
          </button>
        );
      })}
    </div>
  );
}
